import { useState, useEffect, useCallback } from "react";
import API from "../api";
import BiAccessPanel from "../components/BiAccessPanel";
import MassEmailPanel from "../components/MassEmailPanel";
import AnnouncementsPanel from "../components/AnnouncementsPanel";
import { useAuth } from "../context/AuthContext";
import useIsMobile from "../hooks/useIsMobile";
import { ShieldAlert, Check, Trash2, RefreshCw, Mail, Terminal, ServerCog, X,
         AlertTriangle, Bot, Activity, Cpu, Gauge, Inbox, CheckCircle2, UserPlus, UserMinus, FileWarning, Lightbulb, Clock, BarChart3, Megaphone, BellRing } from "lucide-react";

const panel = { background:"var(--t-surface)", border:"1px solid var(--t-border)", borderRadius:14, padding:18 };
const card  = { background:"var(--t-surface-mid)", border:"1px solid var(--t-border)", borderRadius:10 };
const btn   = { display:"inline-flex", alignItems:"center", gap:6, padding:"8px 14px", borderRadius:10, fontSize:13, fontWeight:600, cursor:"pointer", border:"1px solid rgba(79,111,255,0.25)", background:"rgba(79,111,255,0.08)", color:"var(--btn-remote-text, #cdd9ef)", transition:"all 0.2s ease" };

const STATUS = {
  green:  { c:"#22c55e", label:"Operational" },
  yellow: { c:"#f59e0b", label:"Degraded" },
  red:    { c:"#ef4444", label:"Down" },
};

function timeAgo(ts) {
  if (!ts) return "";
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
}

function describeAction(type, payload) {
  const p = payload || {};
  if (type === "service_action") return { label: `Restart Windows service: ${p.name || "(unknown)"}`, code: null };
  if (type === "run_script")     return { label: `Run ${p.language || "powershell"} on device`, code: p.code || "" };
  return { label: type, code: typeof p === "object" ? JSON.stringify(p) : String(p) };
}

// Minimal markdown → JSX: **bold**, `code`, and -/1. lists. Keeps AI plans readable
// instead of dumping raw asterisks/backticks.
function MarkdownLite({ text }) {
  const inline = (s) => {
    const parts = []; let rest = String(s), key = 0;
    const re = /(\*\*([^*]+)\*\*|`([^`]+)`)/;
    let m;
    while ((m = rest.match(re))) {
      if (m.index > 0) parts.push(rest.slice(0, m.index));
      if (m[2] !== undefined) parts.push(<strong key={key++} style={{ color:"var(--t-text-primary)" }}>{m[2]}</strong>);
      else parts.push(<code key={key++} style={{ background:"rgba(129,140,248,0.14)", border:"1px solid rgba(129,140,248,0.28)", borderRadius:4, padding:"0 5px", fontSize:"0.9em", fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace", color:"#a5b4fc" }}>{m[3]}</code>);
      rest = rest.slice(m.index + m[0].length);
    }
    if (rest) parts.push(rest);
    return parts;
  };
  const out = []; let list = null;
  String(text).split("\n").forEach((ln, i) => {
    const li = ln.match(/^\s*(?:[-*]|\d+\.)\s+(.*)/);
    if (li) { (list ||= []).push(<li key={i} style={{ marginBottom:3 }}>{inline(li[1])}</li>); return; }
    if (list) { out.push(<ul key={"ul"+i} style={{ margin:"4px 0 8px", paddingLeft:18 }}>{list}</ul>); list = null; }
    if (ln.trim() === "") { out.push(<div key={i} style={{ height:7 }} />); return; }
    out.push(<div key={i} style={{ marginBottom:4 }}>{inline(ln)}</div>);
  });
  if (list) out.push(<ul key="ul-last" style={{ margin:"4px 0 8px", paddingLeft:18 }}>{list}</ul>);
  return <>{out}</>;
}

// Collapsible "console" panel for an AI remediation plan.
function PlanBox({ text }) {
  const [open, setOpen] = useState(false);
  const clean = String(text).replace(/^🤖 MIDTC Remediation AI[^\n]*\n+/, "");
  return (
    <div style={{ marginTop:10, borderRadius:10, overflow:"hidden", border:"1px solid rgba(129,140,248,0.30)",
      background:"linear-gradient(180deg, rgba(129,140,248,0.06), rgba(2,6,23,0.35))" }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 12px", background:"rgba(129,140,248,0.10)", borderBottom:"1px solid rgba(129,140,248,0.20)" }}>
        <Bot size={13} color="#818cf8" />
        <span style={{ fontSize:10, fontWeight:800, letterSpacing:".14em", color:"#a5b4fc", textTransform:"uppercase", fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace" }}>AI Remediation</span>
        <span style={{ marginLeft:"auto", width:7, height:7, borderRadius:"50%", background:"#818cf8", boxShadow:"0 0 8px #818cf8" }} />
      </div>
      <div style={{ position:"relative", maxHeight: open ? 2000 : 150, overflow:"hidden", padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"var(--t-text-secondary)" }}>
        <MarkdownLite text={clean} />
        {!open && <div style={{ position:"absolute", left:0, right:0, bottom:0, height:52, background:"linear-gradient(180deg, transparent, var(--t-surface-mid))" }} />}
      </div>
      <button onClick={() => setOpen(o => !o)} style={{ width:"100%", border:"none", borderTop:"1px solid rgba(129,140,248,0.20)", background:"transparent", color:"#818cf8", fontSize:11.5, fontWeight:700, padding:"7px", cursor:"pointer" }}>
        {open ? "Show less ▴" : "Show full plan ▾"}
      </button>
    </div>
  );
}

function PanelHeader({ icon, title, count, countColor, children }) {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12, marginBottom:12 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        {icon}
        <h2 style={{ fontSize:15, fontWeight:700, color:"var(--t-text-primary)", margin:0 }}>{title}</h2>
        {count > 0 && (
          <span style={{ background:countColor+"22", color:countColor, fontSize:12, fontWeight:600, padding:"2px 10px", borderRadius:999 }}>{count}</span>
        )}
      </div>
      {children}
    </div>
  );
}

// KPI stat tile for the top of the dashboard. Clickable when it maps to a tab.
function Kpi({ icon, label, value, sub, color, accent, onClick }) {
  return (
    <div onClick={onClick} style={{ ...card, padding:"16px 16px", display:"flex", flexDirection:"column", gap:8, borderTop:`3px solid ${accent || "var(--t-border-strong)"}`, cursor: onClick ? "pointer" : "default" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <span style={{ fontSize:11.5, fontWeight:600, color:"var(--t-text-muted)", textTransform:"uppercase", letterSpacing:".05em" }}>{label}</span>
        {icon}
      </div>
      <div style={{ fontSize:26, fontWeight:800, color: color || "var(--t-text-primary)", lineHeight:1, fontVariantNumeric:"tabular-nums", textShadow: color ? `0 0 18px ${color}55` : "none" }}>{value}</div>
      {sub && <div style={{ fontSize:11.5, color:"var(--t-text-faint)" }}>{sub}</div>}
    </div>
  );
}

// Tab bar separating the review queues into their own views.
function TabBar({ tabs, active, onSelect }) {
  return (
    <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:18, borderBottom:"1px solid var(--t-border)", paddingBottom:0 }}>
      {tabs.map(t => {
        const is = t.key === active;
        return (
          <button key={t.key} onClick={() => onSelect(t.key)} style={{
            display:"inline-flex", alignItems:"center", gap:7, padding:"10px 16px", fontSize:13, fontWeight:600,
            cursor:"pointer", background:"transparent", border:"none",
            borderBottom: is ? `2px solid ${t.color}` : "2px solid transparent",
            color: is ? "var(--t-text-primary)" : "var(--t-text-muted)", marginBottom:-1, transition:"all 0.15s ease",
          }}>
            {t.icon}
            {t.label}
            {t.count > 0 && (
              <span style={{ background:t.color+"22", color:t.color, fontSize:11.5, fontWeight:700, padding:"1px 8px", borderRadius:999 }}>{t.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default function AdminReview() {
  const isMobile = useIsMobile();
  const [status, setStatus]     = useState(null);
  const [stLoading, setStLd]    = useState(true);
  const [actions, setActions]   = useState([]);
  const [actBusy, setActBusy]   = useState(null);
  const [actLoading, setActLd]  = useState(true);
  const [issues, setIssues]     = useState([]);
  const [issLoading, setIssLd]  = useState(true);
  const [items, setItems]       = useState([]);
  const [unreviewed, setUnrev]  = useState(0);
  const [qLoading, setQLd]      = useState(true);
  const [showReviewed, setShow] = useState(false);
  const [qBusy, setQBusy]       = useState(null);
  const [provisions, setProv]   = useState([]);
  const [provLoading, setProvLd]= useState(true);
  const [provBusy, setProvBusy] = useState(null);
  const [offboards, setOff]     = useState([]);
  const [offLoading, setOffLd]  = useState(true);
  const [offBusy, setOffBusy]   = useState(null);
  const [files, setFiles]       = useState([]);
  const [filesLoading, setFLd]  = useState(true);
  const [fileBusy, setFileBusy] = useState(null);
  const [features, setFeatures] = useState([]);
  const [featLoading, setFeatLd]= useState(true);
  const [featBusy, setFeatBusy] = useState(null);
  const [ftr, setFtr]           = useState({ tickets: [], this_week: { ftr_completed:0, completed_total:0, ftr_percent:0 }, weekly: [], ftr_all_time: 0 });
  const [ftrLoading, setFtrLd]  = useState(true);
  const [diffView, setDiffView] = useState(null);   // { jobId, diff } — expanded AI-build diff
  const [tab, setTab]           = useState("status");
  // Mass Email sends to clients, so it is admin-only (the API enforces the same) even though
  // Admin Review itself can be granted to other roles page by page.
  const { agent } = useAuth();
  const isAdmin = (agent?.roles?.length ? agent.roles : [agent?.role]).includes("admin");
  const [biFailed, setBiFailed] = useState(0);   // MIDTC BI requests that need a tech
  const onBiCounts = useCallback((c) => setBiFailed(c.failed || 0), []);
  // Dev Agent Requests — cost BEFORE you run anything (automatic builds are off).
  const [devReq, setDevReq]     = useState({ requests: [], budget: null, history: [] });
  const [devLd, setDevLd]       = useState(true);

  const loadStatus = useCallback(() => {
    setStLd(true);
    API.get("/admin/system-status").then(r => setStatus(r.data)).catch(e => console.error(e)).finally(() => setStLd(false));
  }, []);
  const loadActions = useCallback(() => {
    setActLd(true);
    API.get("/ftr/pending-actions").then(r => setActions(r.data || [])).catch(e => console.error(e)).finally(() => setActLd(false));
  }, []);
  const loadIssues = useCallback(() => {
    setIssLd(true);
    API.get("/ftr/active-issues").then(r => setIssues(r.data || [])).catch(e => console.error(e)).finally(() => setIssLd(false));
  }, []);
  const loadRejections = useCallback(() => {
    setQLd(true);
    API.get(`/inbound-rejections?reviewed=${showReviewed}`)
      .then(r => { setItems(r.data.items || []); setUnrev(r.data.unreviewed || 0); })
      .catch(e => console.error(e)).finally(() => setQLd(false));
  }, [showReviewed]);

  const loadProvisions = useCallback(() => {
    setProvLd(true);
    API.get("/admin/pending-provisions").then(r => setProv(r.data || [])).catch(e => console.error(e)).finally(() => setProvLd(false));
  }, []);

  const loadOffboards = useCallback(() => {
    setOffLd(true);
    API.get("/admin/pending-offboards").then(r => setOff(r.data || [])).catch(e => console.error(e)).finally(() => setOffLd(false));
  }, []);

  const loadFileQ = useCallback(() => {
    setFLd(true);
    API.get("/admin/attachment-quarantine").then(r => setFiles(r.data || [])).catch(e => console.error(e)).finally(() => setFLd(false));
  }, []);
  const loadFeatures = useCallback(() => {
    setFeatLd(true);
    API.get("/admin/feature-requests").then(r => setFeatures(r.data || [])).catch(e => console.error(e)).finally(() => setFeatLd(false));
    API.get("/admin/dev-agent-requests")
      .then(r => setDevReq(r.data || { requests: [], budget: null, history: [] }))
      .catch(e => console.error(e)).finally(() => setDevLd(false));
  }, []);

  const loadFtr = useCallback(() => {
    setFtrLd(true);
    API.get("/admin/ftr-tickets")
      .then(r => setFtr(r.data || { tickets: [], this_week: { ftr_completed:0, completed_total:0, ftr_percent:0 }, weekly: [], ftr_all_time: 0 }))
      .catch(e => console.error(e)).finally(() => setFtrLd(false));
  }, []);

  const refreshAll = useCallback(() => { loadStatus(); loadActions(); loadIssues(); loadRejections(); loadProvisions(); loadOffboards(); loadFileQ(); loadFeatures(); loadFtr(); }, [loadStatus, loadActions, loadIssues, loadRejections, loadProvisions, loadOffboards, loadFileQ, loadFeatures, loadFtr]);

  useEffect(() => { loadStatus(); const t = setInterval(loadStatus, 60000); return () => clearInterval(t); }, [loadStatus]);
  useEffect(() => { loadActions(); }, [loadActions]);
  useEffect(() => { API.get("/admin/bi-integration").then(r => setBiFailed(r.data.counts?.failed || 0)).catch(() => {}); }, []);
  useEffect(() => { loadIssues(); }, [loadIssues]);
  useEffect(() => { loadRejections(); }, [loadRejections]);
  useEffect(() => { loadProvisions(); }, [loadProvisions]);
  useEffect(() => { loadOffboards(); }, [loadOffboards]);
  useEffect(() => { loadFileQ(); }, [loadFileQ]);
  useEffect(() => { loadFeatures(); }, [loadFeatures]);
  useEffect(() => { loadFtr(); }, [loadFtr]);

  const approve = async (id) => {
    setActBusy(id);
    try { await API.post(`/device-commands/${id}/approve`); loadActions(); loadStatus(); }
    catch (e) { alert(e.response?.data?.error || "Failed to approve"); }
    finally { setActBusy(null); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Reject this AI-proposed action? Optional reason for the ticket note:");
    if (reason === null) return;
    setActBusy(id);
    try { await API.post(`/device-commands/${id}/reject`, { reason }); loadActions(); }
    catch (e) { alert(e.response?.data?.error || "Failed to reject"); }
    finally { setActBusy(null); }
  };
  const approveProvision = async (id) => {
    if (!window.confirm("Create this Microsoft 365 account and email the requester the temporary password?\n\nMake sure a license seat is free in the tenant first.")) return;
    setProvBusy(id);
    try {
      const r = await API.post(`/admin/pending-provisions/${id}/approve`);
      alert(`Account created: ${r.data.upn} (${r.data.license})${r.data.license_warning ? "\n\n⚠️ " + r.data.license_warning : ""}`);
      loadProvisions();
    } catch (e) { alert(e.response?.data?.error || "Failed to approve"); }
    finally { setProvBusy(null); }
  };
  const rejectProvision = async (id) => {
    const reason = window.prompt("Reject this new-user request? Optional reason for the ticket note:");
    if (reason === null) return;
    setProvBusy(id);
    try { await API.post(`/admin/pending-provisions/${id}/reject`, { reason }); loadProvisions(); }
    catch (e) { alert(e.response?.data?.error || "Failed to reject"); }
    finally { setProvBusy(null); }
  };
  const resolveFeature = async (id, status) => {
    if (status === "dismissed" && !window.confirm("Dismiss this feature request (won't do)?")) return;
    setFeatBusy(id);
    try { await API.post(`/admin/feature-requests/${id}/resolve`, { status }); loadFeatures(); }
    catch (e) { alert(e.response?.data?.error || "Failed to update"); }
    finally { setFeatBusy(null); }
  };
  // ── AD Server AI dev agent (adserverFtrDev.js): build / review / deploy ──
  const implementFeature = async (id) => {
    if (!window.confirm("Start the AI dev agent on this AD Server feature request?\n\nIt codes against the staged copy — nothing goes live until you deploy the result here.")) return;
    setFeatBusy(id);
    try { await API.post(`/admin/feature-requests/${id}/implement`); alert("AI build started — it usually takes a few minutes. Refresh this tab to see the result."); loadFeatures(); }
    catch (e) { alert(e.response?.data?.error || "Failed to start the AI build"); }
    finally { setFeatBusy(null); }
  };
  const viewJobDiff = async (jobId) => {
    if (diffView?.jobId === jobId) { setDiffView(null); return; }
    setDiffView({ jobId, diff: null });
    try { const r = await API.get(`/admin/adserver-ftr/${jobId}`); setDiffView({ jobId, diff: r.data.diff || "(empty diff)" }); }
    catch (e) { setDiffView(null); alert(e.response?.data?.error || "Failed to load the diff"); }
  };
  const deployJob = async (f) => {
    if (!window.confirm(`Deploy the AI-built change for “${f.title}” to the LIVE ad server?\n\nEach file is lint-checked and the site smoke-checked after the copy, with automatic rollback if either fails.`)) return;
    setFeatBusy(f.id);
    try {
      const r = await API.post(`/admin/adserver-ftr/${f.job_id}/deploy`);
      alert(`Deployed ${r.data.files.length} file(s) to the live ad server.\nThe feature request is marked done and the ticket has been noted.`);
      setDiffView(null); loadFeatures();
    } catch (e) { alert(e.response?.data?.error || "Deploy failed"); }
    finally { setFeatBusy(null); }
  };
  const discardJob = async (f) => {
    if (!window.confirm("Discard the AI-built change? The staged files are restored and the feature request stays open.")) return;
    setFeatBusy(f.id);
    try { await API.post(`/admin/adserver-ftr/${f.job_id}/discard`); setDiffView(null); loadFeatures(); }
    catch (e) { alert(e.response?.data?.error || "Failed to discard"); }
    finally { setFeatBusy(null); }
  };
  const approveOffboard = async (id) => {
    if (!window.confirm("Reclaim this user's Microsoft 365 license seats now?\n\nDo any mailbox handover (e.g. delegate to their manager or convert to shared) in the tenant FIRST — removing the license can start the data-retention clock on the mailbox.\n\nSign-in is already blocked and sessions revoked.")) return;
    setOffBusy(id);
    try {
      const r = await API.post(`/admin/pending-offboards/${id}/approve`);
      alert(`Offboarding completed for ${r.data.upn}.\n\n${r.data.seats_freed ? `Freed ${r.data.seats_freed} seat(s): ${r.data.reclaimed.join(", ")}` : "No licenses remained to reclaim."}`);
      loadOffboards();
    } catch (e) { alert(e.response?.data?.error || "Failed to approve"); }
    finally { setOffBusy(null); }
  };
  const rejectOffboard = async (id) => {
    const reason = window.prompt("Leave this user's licenses in place (skip reclamation)? Optional reason for the ticket note:");
    if (reason === null) return;
    setOffBusy(id);
    try { await API.post(`/admin/pending-offboards/${id}/reject`, { reason }); loadOffboards(); }
    catch (e) { alert(e.response?.data?.error || "Failed to reject"); }
    finally { setOffBusy(null); }
  };
  const releaseFile = async (id) => {
    if (!window.confirm("Release this quarantined file and attach it to the ticket?\n\nOnly do this if you're sure the file is safe — it was flagged by the malware scanner.")) return;
    setFileBusy(id);
    try { await API.post(`/admin/attachment-quarantine/${id}/release`); loadFileQ(); }
    catch (e) { alert(e.response?.data?.error || "Failed to release"); }
    finally { setFileBusy(null); }
  };
  const deleteFile = async (id) => {
    if (!window.confirm("Permanently delete this quarantined file? This cannot be undone.")) return;
    setFileBusy(id);
    try { await API.post(`/admin/attachment-quarantine/${id}/delete`); loadFileQ(); }
    catch (e) { alert(e.response?.data?.error || "Failed to delete"); }
    finally { setFileBusy(null); }
  };
  const markReviewed = async (id, reviewed) => {
    setQBusy(id);
    try { await API.post(`/inbound-rejections/${id}/reviewed`, { reviewed }); loadRejections(); }
    catch (e) { alert(e.response?.data?.error || "Failed"); }
    finally { setQBusy(null); }
  };
  const removeRejection = async (id) => {
    if (!window.confirm("Delete this quarantined email record? This cannot be undone.")) return;
    setQBusy(id);
    try { await API.delete(`/inbound-rejections/${id}`); loadRejections(); }
    catch (e) { alert(e.response?.data?.error || "Failed"); }
    finally { setQBusy(null); }
  };

  const provPending = provisions.filter(p => p.status === "awaiting_approval");
  const provHistory = provisions.filter(p => p.status !== "awaiting_approval");
  const offPending   = offboards.filter(p => p.status === "awaiting_approval");
  const offScheduled = offboards.filter(p => p.status === "scheduled");
  const offHistory   = offboards.filter(p => p.status !== "awaiting_approval" && p.status !== "scheduled");
  const filePending = files.filter(f => f.status === "quarantined");
  const fileHistory = files.filter(f => f.status !== "quarantined");
  const featOpen    = features.filter(f => f.status === "open");
  const featHistory = features.filter(f => f.status !== "open");

  const overall = status?.overall || "green";
  const checks  = status?.checks || [];
  const greenCount = checks.filter(c => c.status === "green").length;
  const uptime24 = status?.overall_uptime?.["24h"];
  const overallLabel = overall === "green" ? "All systems operational" : overall === "yellow" ? "Degraded — some issues" : "Service issue detected";

  return (
    <div style={{ padding: isMobile ? 14 : 24, maxWidth:1320, margin:"0 auto" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12, marginBottom:4 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <ShieldAlert size={22} color="#f59e0b" />
          <h1 style={{ fontSize:22, fontWeight:700, color:"var(--t-text-primary)", margin:0 }}>Operations Dashboard</h1>
          <span style={{ display:"inline-flex", alignItems:"center", gap:7, fontSize:13, fontWeight:600, color:STATUS[overall].c, marginLeft:6 }}>
            <span style={{ width:9, height:9, borderRadius:"50%", background:STATUS[overall].c, boxShadow:`0 0 8px ${STATUS[overall].c}` }} />
            {overallLabel}
          </span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          {status?.generated_at && <span style={{ fontSize:11.5, color:"var(--t-text-faint)" }}>Updated {timeAgo(status.generated_at)}</span>}
          <button style={btn} onClick={refreshAll}><RefreshCw size={14}/> Refresh all</button>
        </div>
      </div>
      <p style={{ color:"var(--t-text-muted)", fontSize:13, marginTop:0, marginBottom:20 }}>
        Live system health, active errors, and items awaiting your approval.
      </p>

      {/* ───────── KPI ROW (always visible — click to jump to a tab) ───────── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:12, marginBottom:18 }}>
        <Kpi label="System health" accent={STATUS[overall].c} color={STATUS[overall].c} onClick={() => setTab("status")}
          icon={<Activity size={16} color={STATUS[overall].c} />}
          value={STATUS[overall].label} sub={`${greenCount}/${checks.length || 0} services healthy`} />
        <Kpi label="Uptime (24h)" accent="#22c55e" icon={<Gauge size={16} color="#22c55e" />} onClick={() => setTab("status")}
          value={uptime24 != null ? `${uptime24.toFixed(2)}%` : "—"} sub="Across all services" />
        <Kpi label="Active errors" accent={issues.length ? "#ef4444" : "#22c55e"} color={issues.length ? "#ef4444" : undefined} onClick={() => setTab("errors")}
          icon={<AlertTriangle size={16} color={issues.length ? "#ef4444" : "#22c55e"} />}
          value={issues.length} sub={issues.length ? "Need attention" : "None detected"} />
        <Kpi label="Pending approvals" accent={actions.length ? "#6366f1" : "var(--t-border-strong)"} color={actions.length ? "#818cf8" : undefined} onClick={() => setTab("approvals")}
          icon={<ServerCog size={16} color="#818cf8" />}
          value={actions.length} sub="AI-proposed actions" />
        <Kpi label="User provisioning" accent={provPending.length ? "#06b6d4" : "var(--t-border-strong)"} color={provPending.length ? "#22d3ee" : undefined} onClick={() => setTab("provisioning")}
          icon={<UserPlus size={16} color="#22d3ee" />}
          value={provPending.length} sub={provHistory.length ? `${provHistory.length} handled recently` : "Awaiting approval"} />
        <Kpi label="Offboarding" accent={offPending.length ? "#f43f5e" : "var(--t-border-strong)"} color={offPending.length ? "#fb7185" : undefined} onClick={() => setTab("offboarding")}
          icon={<UserMinus size={16} color="#fb7185" />}
          value={offPending.length} sub={offHistory.length ? `${offHistory.length} handled recently` : "Reclaim license seats"} />
        <Kpi label="File quarantine" accent={filePending.length ? "#ef4444" : "var(--t-border-strong)"} color={filePending.length ? "#f87171" : undefined} onClick={() => setTab("filequarantine")}
          icon={<FileWarning size={16} color="#f87171" />}
          value={filePending.length} sub={fileHistory.length ? `${fileHistory.length} handled recently` : "Malware-flagged files"} />
        <Kpi label="FTR completed (wk)" accent={ftr.this_week.ftr_completed ? "#16a34a" : "var(--t-border-strong)"} color={ftr.this_week.ftr_completed ? "#22c55e" : undefined} onClick={() => setTab("ftrtickets")}
          icon={<Bot size={16} color="#22c55e" />}
          value={`${ftr.this_week.ftr_percent}%`} sub={`${ftr.this_week.ftr_completed} of ${ftr.this_week.completed_total} closed`} />
        <Kpi label="Email quarantine" accent={unreviewed ? "#d97706" : "var(--t-border-strong)"} color={unreviewed ? "#f59e0b" : undefined} onClick={() => setTab("quarantine")}
          icon={<Inbox size={16} color="#f59e0b" />}
          value={unreviewed} sub="Awaiting review" />
        <Kpi label="Feature requests" accent={featOpen.length ? "#a855f7" : "var(--t-border-strong)"} color={featOpen.length ? "#c084fc" : undefined} onClick={() => setTab("features")}
          icon={<Lightbulb size={16} color="#c084fc" />}
          value={featOpen.length} sub={featHistory.length ? `${featHistory.length} handled recently` : "From FTR / customers"} />
        <Kpi label="Dev Agent Requests"
             accent={(devReq.requests || []).some(r => r.status === "open") ? "#f59e0b" : "var(--t-border-strong)"}
             color={(devReq.requests || []).some(r => r.status === "open") ? "#fbbf24" : undefined}
             icon={<Bot size={16} color="#fbbf24" />} onClick={() => setTab("devagent")}
             value={(devReq.requests || []).filter(r => r.status === "open").length}
             sub={devReq.budget ? `$${devReq.budget.remaining_usd.toFixed(2)} of $${devReq.budget.cap_usd.toFixed(2)} left today` : "Cost before you run"} />
        <Kpi label="Agents online" accent="#22c55e" icon={<Cpu size={16} color="#60a5fa" />} onClick={() => setTab("status")}
          value={(checks.find(c => c.name === "RMM agent fleet")?.detail || "—").split(" ")[0]} sub="Last 15 min" />
      </div>

      {/* ───────── TABS ───────── */}
      <TabBar active={tab} onSelect={setTab} tabs={[
        { key:"status",     label:"System Status",   icon:<Activity size={15}/>,      color:STATUS[overall].c, count:0 },
        { key:"errors",     label:"Active Errors",   icon:<AlertTriangle size={15}/>, color:"#ef4444",         count:issues.length },
        { key:"approvals",  label:"Approvals",       icon:<ServerCog size={15}/>,     color:"#818cf8",         count:actions.length },
        { key:"provisioning", label:"User Provisioning", icon:<UserPlus size={15}/>,  color:"#22d3ee",         count:provPending.length },
        { key:"offboarding", label:"Offboarding", icon:<UserMinus size={15}/>,        color:"#fb7185",         count:offPending.length },
        { key:"filequarantine", label:"File Quarantine", icon:<FileWarning size={15}/>, color:"#ef4444",       count:filePending.length },
        { key:"quarantine", label:"Email Quarantine",icon:<Mail size={15}/>,          color:"#f59e0b",         count:unreviewed },
        { key:"features",   label:"Feature Requests",icon:<Lightbulb size={15}/>,     color:"#a855f7",         count:featOpen.length },
        { key:"ftrtickets", label:"FTR Tickets",     icon:<Bot size={15}/>,           color:"#22c55e",         count:ftr.this_week.ftr_completed },
        { key:"bi",         label:"MIDTC BI",        icon:<BarChart3 size={15}/>,     color:"#38bdf8",         count:biFailed },
        ...(isAdmin ? [{ key:"massemail", label:"Mass Email", icon:<Megaphone size={15}/>, color:"#2563eb", count:0 }] : []),
        ...(isAdmin ? [{ key:"announcements", label:"Announcements", icon:<BellRing size={15}/>, color:"#f59e0b", count:0 }] : []),
      ]} />

      {/* ───────── SYSTEM STATUS TAB ───────── */}
      {tab === "status" && (
      <div style={{ ...panel, borderLeft:`3px solid ${STATUS[overall].c}`, marginBottom:18 }}>
        <PanelHeader icon={<Activity size={18} color={STATUS[overall].c} />} title="System status">
          <button style={btn} onClick={loadStatus}><RefreshCw size={14}/> Refresh</button>
        </PanelHeader>

        <div style={{ display:"flex", gap:16, flexWrap:"wrap", marginBottom:14 }}>
          {Object.entries(STATUS).map(([k,v]) => (
            <span key={k} style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:12, color:"var(--t-text-muted)" }}>
              <span style={{ width:8, height:8, borderRadius:"50%", background:v.c }} /> {v.label}
            </span>
          ))}
        </div>

        {status?.overall_uptime && (
          <div style={{ ...card, display:"flex", flexWrap:"wrap", padding:"12px 4px", marginBottom:14 }}>
            {[["24h","Last 24 hours"],["7d","Last 7 days"],["30d","Last 30 days"],["90d","Last 90 days"]].map(([k,label]) => (
              <div key={k} style={{ flex:"1 1 120px", textAlign:"center", padding:"4px 8px" }}>
                <div style={{ fontSize:20, fontWeight:800, color:"var(--t-text-primary)", fontVariantNumeric:"tabular-nums" }}>{(status.overall_uptime[k] ?? 100).toFixed(3)}%</div>
                <div style={{ fontSize:11, color:"var(--t-text-muted)", marginTop:2 }}>{label}</div>
              </div>
            ))}
          </div>
        )}

        {stLoading && !status ? (
          <div style={{ padding:20, textAlign:"center", color:"var(--t-text-muted)" }}>Checking…</div>
        ) : (
          <div style={{ ...card, overflow:"hidden" }}>
            {checks.map((c, idx) => {
              const up = c.uptime?.["90d"];
              return (
                <div key={c.name} style={{ display:"flex", alignItems:"center", flexWrap: isMobile ? "wrap" : "nowrap", gap:12, padding:"11px 14px", borderTop: idx ? "1px solid var(--t-border)" : "none" }}>
                  <span style={{ width:10, height:10, borderRadius:"50%", flexShrink:0, background:STATUS[c.status].c, boxShadow:`0 0 7px ${STATUS[c.status].c}99` }} />
                  <div style={isMobile ? { flex:"1 1 auto", minWidth:0 } : { minWidth:160, flex:"0 0 auto" }}>
                    <div style={{ fontSize:13, fontWeight:600, color:"var(--t-text-primary)" }}>{c.name}</div>
                    <div style={{ fontSize:11, color:"var(--t-text-muted)", marginTop:1 }}>{c.detail}</div>
                  </div>
                  <div style={{ flex: isMobile ? "1 1 100%" : 1, order: isMobile ? 3 : 0, display:"flex", alignItems:"center", gap:1.5, height:22, minWidth:80, overflow:"hidden", justifyContent:"flex-end" }}>
                    {(c.history || []).slice(-90).map((h, i) => (
                      <span key={i} title={`${h.day}: ${STATUS[h.status]?.label || h.status}`} style={{ flex:"1 1 2px", maxWidth:5, height:"100%", borderRadius:1, background:(STATUS[h.status]?.c || "#475569") }} />
                    ))}
                    {(!c.history || c.history.length === 0) && <span style={{ fontSize:10, color:"var(--t-text-faint)" }}>collecting history…</span>}
                  </div>
                  <div style={{ flex:"0 0 auto", textAlign:"right", minWidth:96 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:STATUS[c.status].c }}>{STATUS[c.status].label}</div>
                    {up != null && <div style={{ fontSize:10.5, color:"var(--t-text-faint)" }}>{up.toFixed(2)}% · 90d</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* ───────── ACTIVE ERRORS TAB ───────── */}
      {tab === "errors" && (
        <div style={panel}>
          <PanelHeader icon={<AlertTriangle size={18} color="#ef4444" />} title="Active errors" count={issues.length} countColor="#ef4444">
            <button style={btn} onClick={loadIssues}><RefreshCw size={14}/></button>
          </PanelHeader>
          {issLoading ? (
            <div style={{ ...card, padding:24, textAlign:"center", color:"var(--t-text-muted)" }}>Loading…</div>
          ) : issues.length === 0 ? (
            <div style={{ ...card, padding:28, textAlign:"center", color:"var(--t-text-muted)" }}>
              <CheckCircle2 size={26} style={{ opacity:0.7, marginBottom:6, color:"#22c55e" }} /><div>No active errors. 🎉</div>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {issues.map(iss => {
                const sc = iss.severity === "critical" ? "#dc2626" : iss.severity === "high" ? "#ea580c" : iss.severity === "medium" ? "#d97706" : "#64748b";
                return (
                  <div key={iss.id} style={{ ...card, padding:13, borderLeft:`3px solid ${sc}` }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:4 }}>
                      <span style={{ background:sc, color:"#fff", fontSize:11, fontWeight:700, padding:"2px 9px", borderRadius:999, textTransform:"uppercase" }}>{iss.severity}</span>
                      <span style={{ fontSize:13.5, fontWeight:700, color:"var(--t-text-primary)" }}>{iss.title}</span>
                      {iss.ftr_status && (
                        <span style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:11, color:"var(--t-text-muted)", border:"1px solid var(--t-border)", borderRadius:999, padding:"1px 8px" }}>
                          <Bot size={11}/> AI {iss.ftr_status}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize:12, color:"var(--t-text-muted)", marginBottom:6 }}>
                      {[iss.company_name, iss.device_name, iss.occurrences>1?`×${iss.occurrences}`:null].filter(Boolean).join(" · ")}
                      {iss.ticket_number ? ` · incident #${iss.ticket_number}` : ""} · {timeAgo(iss.last_seen)}
                    </div>
                    {iss.detail && <div style={{ fontSize:12.5, color:"var(--t-text-secondary)", lineHeight:1.5 }}>{iss.detail}</div>}
                    {iss.remediation_plan && <PlanBox text={iss.remediation_plan} />}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ───────── APPROVALS TAB ───────── */}
      {tab === "approvals" && (
        <div style={panel}>
          <PanelHeader icon={<ServerCog size={18} color="#818cf8" />} title="Pending device actions" count={actions.length} countColor="#6366f1">
            <button style={btn} onClick={loadActions}><RefreshCw size={14}/></button>
          </PanelHeader>
          {actLoading ? (
            <div style={{ ...card, padding:24, textAlign:"center", color:"var(--t-text-muted)" }}>Loading…</div>
          ) : actions.length === 0 ? (
            <div style={{ ...card, padding:28, textAlign:"center", color:"var(--t-text-muted)" }}>
              <ServerCog size={26} style={{ opacity:0.4, marginBottom:6 }} /><div>Nothing awaiting approval.</div>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {actions.map(a => {
                const desc = describeAction(a.type, a.payload);
                return (
                  <div key={a.id} style={{ ...card, padding:13 }}>
                    <div style={{ fontSize:12, color:"var(--t-text-muted)", marginBottom:4 }}>
                      {a.ticket_number ? `Ticket #${a.ticket_number}` : "No ticket"} · {a.company_name || "—"} · {timeAgo(a.created_at)}
                    </div>
                    {a.subject && (
                      <div style={{ color:"var(--t-text-primary)", fontSize:13, marginBottom:6, display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                        {a.source === "system" && (
                          <span style={{ display:"inline-flex", alignItems:"center", gap:4, background:"rgba(239,68,68,0.13)", color:"#ef4444", fontSize:11, fontWeight:700, padding:"1px 8px", borderRadius:999 }}>
                            <AlertTriangle size={11}/> Auto-remediation
                          </span>
                        )}
                        {a.subject}
                      </div>
                    )}
                    {a.remediation_plan && <div style={{ marginBottom:10 }}><PlanBox text={a.remediation_plan} /></div>}
                    <div style={{ display:"flex", alignItems:"center", gap:8, color:"var(--t-text-primary)", fontWeight:600, fontSize:13, marginBottom:10 }}>
                      {a.type === "run_script" ? <Terminal size={15} color="#f59e0b"/> : <ServerCog size={15} color="#f59e0b"/>}
                      {desc.label}
                      <span style={{ color:"var(--t-text-muted)", fontWeight:400 }}>on {a.device_name || a.device_id}</span>
                    </div>
                    {desc.code ? (
                      <pre style={{ margin:"0 0 10px", padding:"8px 10px", background:"var(--t-surface)", border:"1px solid var(--t-border)", borderRadius:8, fontSize:12, color:"var(--t-text-primary)", whiteSpace:"pre-wrap", wordBreak:"break-word" }}>{desc.code}</pre>
                    ) : null}
                    <div style={{ display:"flex", gap:8 }}>
                      <button style={{ ...btn, color:"#fff", background:"#059669", border:"1px solid #059669", flex:1, justifyContent:"center" }} disabled={actBusy===a.id} onClick={() => approve(a.id)}>
                        <Check size={14}/> Approve &amp; run
                      </button>
                      <button style={{ ...btn, color:"#dc2626", justifyContent:"center" }} disabled={actBusy===a.id} onClick={() => reject(a.id)}>
                        <X size={14}/> Reject
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ───────── USER PROVISIONING TAB ───────── */}
      {tab === "provisioning" && (
        <div style={panel}>
          <PanelHeader icon={<UserPlus size={18} color="#22d3ee" />} title="New users awaiting approval" count={provPending.length} countColor="#06b6d4">
            <button style={btn} onClick={loadProvisions}><RefreshCw size={14}/></button>
          </PanelHeader>
          {provLoading ? (
            <div style={{ ...card, padding:24, textAlign:"center", color:"var(--t-text-muted)" }}>Loading…</div>
          ) : provisions.length === 0 ? (
            <div style={{ ...card, padding:28, textAlign:"center", color:"var(--t-text-muted)" }}>
              <UserPlus size={26} style={{ opacity:0.4, marginBottom:6 }} /><div>No new-user activity yet.</div>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {provPending.length === 0 && (
                <div style={{ ...card, padding:16, textAlign:"center", color:"var(--t-text-muted)" }}>
                  <CheckCircle2 size={20} style={{ color:"#22c55e", verticalAlign:"-4px", marginRight:6 }} />Nothing awaiting approval — recent activity below.
                </div>
              )}
              {provPending.map(p => (
                <div key={p.id} style={{ ...card, padding:13, borderLeft:"3px solid #06b6d4" }}>
                  <div style={{ fontSize:12, color:"var(--t-text-muted)", marginBottom:4 }}>
                    {p.ticket_number ? `Ticket #${p.ticket_number}` : "No ticket"} · {p.company_name || "—"} · {timeAgo(p.created_at)}
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8, color:"var(--t-text-primary)", fontWeight:700, fontSize:14, marginBottom:4 }}>
                    <UserPlus size={15} color="#22d3ee"/>
                    {p.display_name} <span style={{ color:"var(--t-text-muted)", fontWeight:500 }}>&lt;{p.user_principal_name}&gt;</span>
                  </div>
                  <div style={{ fontSize:12.5, color:"var(--t-text-secondary)", marginBottom:6 }}>
                    License: <strong style={{ color:"var(--t-text-primary)" }}>{p.license_label}</strong>
                    {p.job_title ? ` · ${p.job_title}` : ""}
                    {p.requested_by ? ` · requested by ${p.requested_by}` : ""}
                  </div>
                  {p.reason && (
                    <div style={{ fontSize:12, color:"#f59e0b", background:"rgba(245,158,11,0.10)", border:"1px solid rgba(245,158,11,0.25)", borderRadius:8, padding:"7px 10px", marginBottom:10 }}>
                      ⚠️ {p.reason} Free or assign a {p.license_label} seat in the tenant before approving.
                    </div>
                  )}
                  <div style={{ display:"flex", gap:8 }}>
                    <button style={{ ...btn, color:"#fff", background:"#0891b2", border:"1px solid #0891b2", flex:1, justifyContent:"center" }} disabled={provBusy===p.id} onClick={() => approveProvision(p.id)}>
                      <Check size={14}/> Create account &amp; email requester
                    </button>
                    <button style={{ ...btn, color:"#dc2626", justifyContent:"center" }} disabled={provBusy===p.id} onClick={() => rejectProvision(p.id)}>
                      <X size={14}/> Reject
                    </button>
                  </div>
                </div>
              ))}
              {provHistory.length > 0 && (
                <>
                  <div style={{ fontSize:11.5, fontWeight:700, color:"var(--t-text-muted)", textTransform:"uppercase", letterSpacing:".05em", marginTop:6 }}>Recently handled</div>
                  {provHistory.map(p => (
                    <div key={p.id} style={{ ...card, padding:11, opacity:0.75, borderLeft:`3px solid ${p.status==="rejected" ? "#dc2626" : "#22c55e"}` }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                        <span style={{ background:(p.status==="rejected" ? "#dc2626" : "#16a34a"), color:"#fff", fontSize:10.5, fontWeight:700, padding:"1px 8px", borderRadius:999, textTransform:"uppercase" }}>{p.status === "completed" ? "auto-created" : p.status}</span>
                        <span style={{ fontSize:13, fontWeight:600, color:"var(--t-text-primary)" }}>{p.display_name}</span>
                        <span style={{ color:"var(--t-text-muted)", fontSize:12, wordBreak:"break-all" }}>&lt;{p.user_principal_name}&gt;</span>
                        <span style={{ marginLeft:"auto", fontSize:11, color:"var(--t-text-faint)" }}>{timeAgo(p.resolved_at || p.created_at)}</span>
                      </div>
                      <div style={{ fontSize:12, color:"var(--t-text-muted)", marginTop:3 }}>
                        {[p.license_label, p.ticket_number ? `ticket #${p.ticket_number}` : null].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ───────── FEATURE REQUESTS TAB ───────── */}
      {tab === "devagent" && (
        <div style={panel}>
          <PanelHeader icon={<Bot size={18} color="#fbbf24" />} title="Dev Agent Requests"
            count={(devReq.requests || []).filter(r => r.status === "open").length} countColor="#f59e0b">
            <button style={btn} onClick={loadFeatures}><RefreshCw size={14}/></button>
          </PanelHeader>

          {devReq.budget && (
            <div style={{ ...card, padding:14, marginBottom:10,
                          borderLeft:`3px solid ${devReq.budget.remaining_usd > 0 ? "#22c55e" : "#ef4444"}` }}>
              <div style={{ display:"flex", gap:24, flexWrap:"wrap", alignItems:"baseline" }}>
                <div><div style={{ fontSize:10.5, color:"var(--t-text-muted)", textTransform:"uppercase", letterSpacing:".05em" }}>Spent (24h)</div>
                  <div style={{ fontSize:20, fontWeight:800 }}>${devReq.budget.spent_24h_usd.toFixed(2)}</div></div>
                <div><div style={{ fontSize:10.5, color:"var(--t-text-muted)", textTransform:"uppercase", letterSpacing:".05em" }}>Daily cap</div>
                  <div style={{ fontSize:20, fontWeight:800 }}>${devReq.budget.cap_usd.toFixed(2)}</div></div>
                <div><div style={{ fontSize:10.5, color:"var(--t-text-muted)", textTransform:"uppercase", letterSpacing:".05em" }}>Remaining</div>
                  <div style={{ fontSize:20, fontWeight:800, color: devReq.budget.remaining_usd > 0 ? "#22c55e" : "#ef4444" }}>
                    ${devReq.budget.remaining_usd.toFixed(2)}</div></div>
              </div>
              <div style={{ fontSize:12, color:"var(--t-text-muted)", marginTop:8 }}>
                Automatic AI builds are <b style={{ color:"#fbbf24" }}>{devReq.budget.autobuild_enabled ? "ON" : "OFF"}</b> — requests are captured here and a human decides whether to run them.
                {devReq.budget.remaining_usd <= 0 && <span style={{ color:"#f87171" }}> Budget is used up; nothing can run until the 24h window frees.</span>}
              </div>
            </div>
          )}

          {devLd ? (
            <div style={{ ...card, padding:24, textAlign:"center", color:"var(--t-text-muted)" }}>Loading…</div>
          ) : (devReq.requests || []).length === 0 ? (
            <div style={{ ...card, padding:28, textAlign:"center", color:"var(--t-text-muted)" }}>
              <Bot size={26} style={{ opacity:0.4, marginBottom:6 }} />
              <div>No dev agent requests yet. When the ad-server FTR finds work that needs a code change, it lands here with its cost before anyone runs it.</div>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {(devReq.requests || []).map(r => {
                const open = r.status === "open";
                const est  = r.estimate || {};
                return (
                  <div key={r.id} style={{ ...card, padding:13, borderLeft:`3px solid ${open ? "#f59e0b" : "var(--t-border-strong)"}`, opacity: open ? 1 : 0.72 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
                      <div style={{ flex:"1 1 320px", minWidth:0 }}>
                        <div style={{ fontSize:12, color:"var(--t-text-muted)", marginBottom:4 }}>
                          <span style={{ background:"rgba(245,158,11,0.15)", color:"#fbbf24", fontSize:10.5, fontWeight:700, padding:"1px 8px", borderRadius:999 }}>
                            {r.kind === "change" ? "CHANGE" : "FEATURE"}
                          </span>
                          <span style={{ marginLeft:8 }}>#{r.id}</span>
                          {r.ticket_number && <span style={{ marginLeft:8 }}>· ticket {r.ticket_number}</span>}
                          {!open && <span style={{ marginLeft:8, textTransform:"uppercase" }}>· {r.status}</span>}
                        </div>
                        <div style={{ fontWeight:700, marginBottom:4 }}>{r.title}</div>
                        <div style={{ fontSize:12.5, color:"var(--t-text-muted)", whiteSpace:"pre-wrap" }}>
                          {String(r.details || "").slice(0, 400)}
                        </div>
                        {r.requested_by && <div style={{ fontSize:11.5, color:"var(--t-text-muted)", marginTop:6 }}>Requested by {r.requested_by}</div>}
                      </div>

                      <div style={{ flex:"0 0 210px", textAlign:"right" }}>
                        {r.already_run ? (
                          <>
                            <div style={{ fontSize:10.5, color:"var(--t-text-muted)", textTransform:"uppercase", letterSpacing:".05em" }}>Actually cost</div>
                            <div style={{ fontSize:22, fontWeight:800, color: r.already_run.status === "deployed" ? "#22c55e" : "#f87171" }}>
                              ${r.already_run.actual_usd.toFixed(2)}
                            </div>
                            <div style={{ fontSize:11.5, color:"var(--t-text-muted)" }}>{r.already_run.status}</div>
                          </>
                        ) : (
                          <>
                            <div style={{ fontSize:10.5, color:"var(--t-text-muted)", textTransform:"uppercase", letterSpacing:".05em" }}>Max this run can cost</div>
                            <div style={{ fontSize:22, fontWeight:800, color: est.ceiling_usd > 0 ? "#fbbf24" : "#f87171" }}>
                              ${Number(est.ceiling_usd || 0).toFixed(2)}
                            </div>
                            <div style={{ fontSize:11.5, color:"var(--t-text-muted)", marginTop:2 }}>
                              typical ${Number(est.typical_usd || 0).toFixed(2)}
                              {est.range_low_usd != null && <> · seen ${Number(est.range_low_usd).toFixed(2)}–${Number(est.range_high_usd).toFixed(2)}</>}
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {open && !r.already_run && (
                      <div style={{ marginTop:10, display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                        <button style={{ ...btn, opacity: est.ceiling_usd > 0 ? 1 : 0.5, cursor: est.ceiling_usd > 0 ? "pointer" : "not-allowed" }}
                          disabled={!(est.ceiling_usd > 0)}
                          onClick={() => {
                            if (!window.confirm(`Run the AI dev agent on "${r.title}"?\n\nThis run will stop at $${Number(est.ceiling_usd).toFixed(2)} (whichever binds first: the per-job budget or what's left of today's $${devReq.budget?.cap_usd?.toFixed(2)} cap).\n\nComparable builds have cost $${Number(est.range_low_usd || 0).toFixed(2)}–$${Number(est.range_high_usd || 0).toFixed(2)}.`)) return;
                            implementFeature(r.id);
                          }}>
                          Run with AI (max ${Number(est.ceiling_usd || 0).toFixed(2)})
                        </button>
                        <span style={{ fontSize:11.5, color:"var(--t-text-muted)" }}>
                          or hand it to Claude Code + a technician — no tokens spent
                        </span>
                      </div>
                    )}
                    {est.basis && open && !r.already_run && (
                      <div style={{ fontSize:11, color:"var(--t-text-muted)", marginTop:8, lineHeight:1.5 }}>{est.basis}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === "features" && (
        <div style={panel}>
          <PanelHeader icon={<Lightbulb size={18} color="#c084fc" />} title="Feature & enhancement requests" count={featOpen.length} countColor="#a855f7">
            <button style={btn} onClick={loadFeatures}><RefreshCw size={14}/></button>
          </PanelHeader>
          {featLoading ? (
            <div style={{ ...card, padding:24, textAlign:"center", color:"var(--t-text-muted)" }}>Loading…</div>
          ) : features.length === 0 ? (
            <div style={{ ...card, padding:28, textAlign:"center", color:"var(--t-text-muted)" }}>
              <Lightbulb size={26} style={{ opacity:0.4, marginBottom:6 }} /><div>No feature requests captured yet. When the FTR logs one — or a customer asks for a capability — it lands here.</div>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {featOpen.length === 0 && (
                <div style={{ ...card, padding:16, textAlign:"center", color:"var(--t-text-muted)" }}>
                  <CheckCircle2 size={20} style={{ color:"#22c55e", verticalAlign:"-4px", marginRight:6 }} />Nothing open — recently handled below.
                </div>
              )}
              {featOpen.map(f => (
                <div key={f.id} style={{ ...card, padding:13, borderLeft:"3px solid #a855f7" }}>
                  <div style={{ fontSize:12, color:"var(--t-text-muted)", marginBottom:4 }}>
                    <span style={{ background:"rgba(168,85,247,0.15)", color:"#c084fc", fontSize:10.5, fontWeight:700, padding:"1px 8px", borderRadius:999 }}>{f.area || "AD Server"}</span>
                    {f.kind === "change" && (
                      <span style={{ background:"rgba(245,158,11,0.15)", color:"#f59e0b", fontSize:10.5, fontWeight:700, padding:"1px 8px", borderRadius:999, marginLeft:6 }}>CHANGE</span>
                    )}
                    {" · "}{f.ticket_number ? `Ticket #${f.ticket_number}` : "No ticket"} · {timeAgo(f.created_at)}
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8, color:"var(--t-text-primary)", fontWeight:700, fontSize:14, marginBottom:4 }}>
                    <Lightbulb size={15} color="#c084fc"/> {f.title}
                  </div>
                  {f.details && <div style={{ fontSize:12.5, color:"var(--t-text-secondary)", whiteSpace:"pre-wrap", marginBottom:6 }}>{f.details}</div>}
                  {f.requested_by && <div style={{ fontSize:12, color:"var(--t-text-muted)", marginBottom:10 }}>Requested by {f.requested_by}</div>}
                  {(f.area || "AD Server") === "AD Server" && (
                    <div style={{ margin:"0 0 10px", padding:"9px 11px", borderRadius:8, background:"rgba(168,85,247,0.07)", border:"1px solid rgba(168,85,247,0.25)" }}>
                      {f.job_status === "running" && (
                        <div style={{ fontSize:12.5, color:"#c084fc" }}>🤖 AI dev agent is building this now — usually a few minutes. Hit ⟳ to check.</div>
                      )}
                      {f.job_status === "ready" && (<>
                        <div style={{ fontSize:12, fontWeight:700, color:"#22c55e", marginBottom:4 }}>🤖 AI implementation READY — review &amp; deploy</div>
                        {f.job_summary && <div style={{ fontSize:12.5, color:"var(--t-text-secondary)", whiteSpace:"pre-wrap", marginBottom:6 }}>{f.job_summary}</div>}
                        <div style={{ fontSize:11.5, color:"var(--t-text-muted)", marginBottom:8 }}>Files: {(f.job_files || []).map(x => `${x.path} (${x.action})`).join(", ")}</div>
                        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                          <button style={{ ...btn, color:"#fff", background:"#16a34a", border:"1px solid #16a34a", justifyContent:"center" }} disabled={featBusy===f.id} onClick={() => deployJob(f)}>
                            <Check size={14}/> Deploy to live
                          </button>
                          <button style={{ ...btn, justifyContent:"center" }} onClick={() => viewJobDiff(f.job_id)}>{diffView?.jobId === f.job_id ? "Hide diff" : "View diff"}</button>
                          <button style={{ ...btn, color:"#dc2626", justifyContent:"center" }} disabled={featBusy===f.id} onClick={() => discardJob(f)}>
                            <X size={14}/> Discard build
                          </button>
                        </div>
                        {diffView?.jobId === f.job_id && (
                          <pre style={{ marginTop:8, maxHeight:420, overflow:"auto", fontSize:11, lineHeight:1.45, background:"rgba(0,0,0,0.35)", padding:10, borderRadius:6, whiteSpace:"pre-wrap", wordBreak:"break-all" }}>{diffView.diff ?? "Loading diff…"}</pre>
                        )}
                      </>)}
                      {f.job_status === "failed" && (<>
                        <div style={{ fontSize:12.5, color:"#f87171", marginBottom:6 }}>🤖 AI build failed: {f.job_error}</div>
                        <button style={{ ...btn, justifyContent:"center" }} disabled={featBusy===f.id} onClick={() => implementFeature(f.id)}>Retry AI build</button>
                      </>)}
                      {(f.job_status === "discarded" || !f.job_status) && (
                        <button style={{ ...btn, color:"#c084fc", border:"1px solid rgba(168,85,247,0.4)", justifyContent:"center" }} disabled={featBusy===f.id} onClick={() => implementFeature(f.id)}>
                          🤖 {f.job_status === "discarded" ? "Rebuild with AI" : "Build with AI"}
                        </button>
                      )}
                    </div>
                  )}
                  <div style={{ display:"flex", gap:8 }}>
                    <button style={{ ...btn, color:"#fff", background:"#9333ea", border:"1px solid #9333ea", justifyContent:"center" }} disabled={featBusy===f.id} onClick={() => resolveFeature(f.id, "done")}>
                      <Check size={14}/> Mark done
                    </button>
                    <button style={{ ...btn, color:"#dc2626", justifyContent:"center" }} disabled={featBusy===f.id} onClick={() => resolveFeature(f.id, "dismissed")}>
                      <X size={14}/> Dismiss
                    </button>
                  </div>
                </div>
              ))}
              {featHistory.length > 0 && (
                <>
                  <div style={{ fontSize:11.5, fontWeight:700, color:"var(--t-text-muted)", textTransform:"uppercase", letterSpacing:".05em", marginTop:6 }}>Recently handled</div>
                  {featHistory.map(f => (
                    <div key={f.id} style={{ ...card, padding:11, opacity:0.75, borderLeft:`3px solid ${f.status==="dismissed" ? "#dc2626" : "#22c55e"}` }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                        <span style={{ background:(f.status==="dismissed" ? "#dc2626" : "#16a34a"), color:"#fff", fontSize:10.5, fontWeight:700, padding:"1px 8px", borderRadius:999, textTransform:"uppercase" }}>{f.status}</span>
                        <span style={{ fontSize:13, fontWeight:600, color:"var(--t-text-primary)" }}>{f.title}</span>
                        <span style={{ marginLeft:"auto", fontSize:11, color:"var(--t-text-faint)" }}>{timeAgo(f.resolved_at || f.created_at)}</span>
                      </div>
                      <div style={{ fontSize:12, color:"var(--t-text-muted)", marginTop:3 }}>
                        {[f.area, f.ticket_number ? `ticket #${f.ticket_number}` : null].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ───────── OFFBOARDING TAB ───────── */}
      {tab === "offboarding" && (
        <div style={panel}>
          <PanelHeader icon={<UserMinus size={18} color="#fb7185" />} title="Offboardings awaiting completion" count={offPending.length} countColor="#f43f5e">
            <button style={btn} onClick={loadOffboards}><RefreshCw size={14}/></button>
          </PanelHeader>
          <p style={{ color:"var(--t-text-muted)", fontSize:12.5, marginTop:0, marginBottom:12 }}>
            The AI now handles terminations end-to-end: it locks the account, converts the mailbox to a <strong>shared mailbox</strong>, and reclaims the license — automatically. The items below only appear when that couldn't finish on its own (convert the mailbox to shared in the Exchange admin center, then reclaim the seats here) or are terminations <strong>scheduled</strong> for a future date.
          </p>
          {offLoading ? (
            <div style={{ ...card, padding:24, textAlign:"center", color:"var(--t-text-muted)" }}>Loading…</div>
          ) : offboards.length === 0 ? (
            <div style={{ ...card, padding:28, textAlign:"center", color:"var(--t-text-muted)" }}>
              <UserMinus size={26} style={{ opacity:0.4, marginBottom:6 }} /><div>No offboarding activity yet.</div>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {offPending.length === 0 && (
                <div style={{ ...card, padding:16, textAlign:"center", color:"var(--t-text-muted)" }}>
                  <CheckCircle2 size={20} style={{ color:"#22c55e", verticalAlign:"-4px", marginRight:6 }} />Nothing awaiting completion — recent activity below.
                </div>
              )}
              {offPending.map(p => {
                const lics = Array.isArray(p.licenses) ? p.licenses : [];
                const cont = p.containment || {};
                const failed = Object.entries(cont).filter(([, v]) => String(v).startsWith("error:"));
                return (
                <div key={p.id} style={{ ...card, padding:13, borderLeft:"3px solid #f43f5e" }}>
                  <div style={{ fontSize:12, color:"var(--t-text-muted)", marginBottom:4 }}>
                    {p.ticket_number ? `Ticket #${p.ticket_number}` : "No ticket"} · {p.company_name || "—"} · {timeAgo(p.created_at)}
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8, color:"var(--t-text-primary)", fontWeight:700, fontSize:14, marginBottom:4 }}>
                    <UserMinus size={15} color="#fb7185"/>
                    {p.display_name} <span style={{ color:"var(--t-text-muted)", fontWeight:500 }}>&lt;{p.user_principal_name}&gt;</span>
                  </div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:6 }}>
                    <span style={{ fontSize:11, background:"rgba(34,197,94,0.12)", color:"#22c55e", border:"1px solid rgba(34,197,94,0.25)", borderRadius:999, padding:"1px 9px" }}>Sign-in blocked</span>
                    <span style={{ fontSize:11, background:"rgba(34,197,94,0.12)", color:"#22c55e", border:"1px solid rgba(34,197,94,0.25)", borderRadius:999, padding:"1px 9px" }}>Sessions revoked</span>
                    <span style={{ fontSize:11, background:"rgba(34,197,94,0.12)", color:"#22c55e", border:"1px solid rgba(34,197,94,0.25)", borderRadius:999, padding:"1px 9px" }}>Password reset</span>
                    {p.auto_reply_set && <span style={{ fontSize:11, background:"rgba(34,197,94,0.12)", color:"#22c55e", border:"1px solid rgba(34,197,94,0.25)", borderRadius:999, padding:"1px 9px" }}>Auto-reply set</span>}
                  </div>
                  <div style={{ fontSize:12.5, color:"var(--t-text-secondary)", marginBottom:6 }}>
                    Seats to reclaim: <strong style={{ color:"var(--t-text-primary)" }}>{lics.length ? lics.map(l => l.label).join(", ") : "none"}</strong>
                    {p.requested_by ? ` · requested by ${p.requested_by}` : ""}
                  </div>
                  {p.manager && p.manager.display_name && (
                    <div style={{ fontSize:12, color:"var(--t-text-secondary)", marginBottom:6 }}>
                      Mailbox handover → manager: <strong style={{ color:"var(--t-text-primary)" }}>{p.manager.display_name}</strong>{p.manager.email ? ` <${p.manager.email}>` : ""}
                    </div>
                  )}
                  {(p.result && (p.result.convert_error || p.result.reclaim_error)) && (
                    <div style={{ fontSize:12, color:"#f43f5e", background:"rgba(244,63,94,0.10)", border:"1px solid rgba(244,63,94,0.28)", borderRadius:8, padding:"7px 10px", marginBottom:10 }}>
                      ⚠️ Automatic {p.result.convert_error ? "shared-mailbox conversion" : "license reclaim"} failed: {p.result.convert_error || p.result.reclaim_error}.
                      {p.result.convert_error ? " Convert the mailbox to Shared in the Exchange admin center, then reclaim the seats below." : " Reclaim the seats below."}
                    </div>
                  )}
                  {failed.length > 0 && (
                    <div style={{ fontSize:12, color:"#f59e0b", background:"rgba(245,158,11,0.10)", border:"1px solid rgba(245,158,11,0.25)", borderRadius:8, padding:"7px 10px", marginBottom:10 }}>
                      ⚠️ Some containment steps errored: {failed.map(([k, v]) => `${k} (${String(v).replace(/^error:\s*/, "")})`).join("; ")}. Verify in the tenant.
                    </div>
                  )}
                  <div style={{ display:"flex", gap:8 }}>
                    <button style={{ ...btn, color:"#fff", background:"#e11d48", border:"1px solid #e11d48", flex:1, justifyContent:"center" }} disabled={offBusy===p.id} onClick={() => approveOffboard(p.id)}>
                      <Check size={14}/> Reclaim license seats
                    </button>
                    <button style={{ ...btn, color:"var(--t-text-secondary)", justifyContent:"center" }} disabled={offBusy===p.id} onClick={() => rejectOffboard(p.id)}>
                      <X size={14}/> Keep licenses
                    </button>
                  </div>
                </div>
              );})}

              {offScheduled.length > 0 && (
                <>
                  <div style={{ fontSize:11.5, fontWeight:700, color:"var(--t-text-muted)", textTransform:"uppercase", letterSpacing:".05em", marginTop:6 }}>Scheduled terminations</div>
                  {offScheduled.map(p => (
                    <div key={p.id} style={{ ...card, padding:13, borderLeft:"3px solid #f59e0b" }}>
                      <div style={{ fontSize:12, color:"var(--t-text-muted)", marginBottom:4 }}>
                        {p.ticket_number ? `Ticket #${p.ticket_number}` : "No ticket"} · {p.company_name || "—"}
                      </div>
                      <div style={{ display:"flex", alignItems:"center", gap:8, color:"var(--t-text-primary)", fontWeight:700, fontSize:14, marginBottom:4 }}>
                        <Clock size={15} color="#f59e0b"/>
                        {p.display_name || p.user_principal_name} <span style={{ color:"var(--t-text-muted)", fontWeight:500 }}>&lt;{p.user_principal_name}&gt;</span>
                      </div>
                      <div style={{ fontSize:12.5, color:"var(--t-text-secondary)", marginBottom:8 }}>
                        Termination scheduled for <strong style={{ color:"var(--t-text-primary)" }}>{p.scheduled_at ? new Date(p.scheduled_at).toLocaleString() : "—"}</strong>. The account will be locked, the mailbox converted to shared, and the license reclaimed automatically at that time.
                      </div>
                      <button style={{ ...btn, color:"var(--t-text-secondary)", justifyContent:"center" }} disabled={offBusy===p.id} onClick={() => rejectOffboard(p.id)}>
                        <X size={14}/> Cancel scheduled termination
                      </button>
                    </div>
                  ))}
                </>
              )}
              {offHistory.length > 0 && (
                <>
                  <div style={{ fontSize:11.5, fontWeight:700, color:"var(--t-text-muted)", textTransform:"uppercase", letterSpacing:".05em", marginTop:6 }}>Recently handled</div>
                  {offHistory.map(p => (
                    <div key={p.id} style={{ ...card, padding:11, opacity:0.75, borderLeft:`3px solid ${p.status==="rejected" ? "#dc2626" : "#22c55e"}` }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                        <span style={{ background:(p.status==="rejected" ? "#dc2626" : "#16a34a"), color:"#fff", fontSize:10.5, fontWeight:700, padding:"1px 8px", borderRadius:999, textTransform:"uppercase" }}>{p.status === "completed" ? "completed" : p.status}</span>
                        <span style={{ fontSize:13, fontWeight:600, color:"var(--t-text-primary)" }}>{p.display_name}</span>
                        <span style={{ color:"var(--t-text-muted)", fontSize:12, wordBreak:"break-all" }}>&lt;{p.user_principal_name}&gt;</span>
                        <span style={{ marginLeft:"auto", fontSize:11, color:"var(--t-text-faint)" }}>{timeAgo(p.resolved_at || p.created_at)}</span>
                      </div>
                      <div style={{ fontSize:12, color:"var(--t-text-muted)", marginTop:3 }}>
                        {[p.result?.seats_freed != null ? `${p.result.seats_freed} seat(s) freed` : null, p.ticket_number ? `ticket #${p.ticket_number}` : null].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ───────── FILE QUARANTINE TAB ───────── */}
      {/* ───────── MIDTC BI TAB (biConnector.js) ───────── */}
      {tab === "bi" && <BiAccessPanel onCounts={onBiCounts} />}

      {tab === "massemail" && isAdmin && <MassEmailPanel />}

      {/* ───────── ANNOUNCEMENTS TAB (announcements.js) — the yellow-orange bar on every page ───────── */}
      {tab === "announcements" && isAdmin && <AnnouncementsPanel />}

      {tab === "filequarantine" && (
        <div style={panel}>
          <PanelHeader icon={<FileWarning size={18} color="#ef4444" />} title="Malware-flagged files" count={filePending.length} countColor="#ef4444">
            <button style={btn} onClick={loadFileQ}><RefreshCw size={14}/></button>
          </PanelHeader>
          {filesLoading ? (
            <div style={{ ...card, padding:24, textAlign:"center", color:"var(--t-text-muted)" }}>Loading…</div>
          ) : files.length === 0 ? (
            <div style={{ ...card, padding:28, textAlign:"center", color:"var(--t-text-muted)" }}>
              <CheckCircle2 size={26} style={{ opacity:0.7, marginBottom:6, color:"#22c55e" }} /><div>No quarantined files. 🎉</div>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {filePending.length === 0 && (
                <div style={{ ...card, padding:16, textAlign:"center", color:"var(--t-text-muted)" }}>
                  <CheckCircle2 size={20} style={{ color:"#22c55e", verticalAlign:"-4px", marginRight:6 }} />Nothing in quarantine right now — recent activity below.
                </div>
              )}
              {filePending.map(f => {
                const vc = f.verdict === "infected" ? "#dc2626" : f.verdict === "blocked_type" ? "#ea580c" : "#d97706";
                const kb = f.size_bytes ? (f.size_bytes < 1048576 ? `${Math.round(f.size_bytes/1024)} KB` : `${(f.size_bytes/1048576).toFixed(1)} MB`) : "";
                return (
                  <div key={f.id} style={{ ...card, padding:13, borderLeft:`3px solid ${vc}` }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:4 }}>
                      <span style={{ background:vc, color:"#fff", fontSize:11, fontWeight:700, padding:"2px 9px", borderRadius:999, textTransform:"uppercase" }}>{f.verdict}</span>
                      <span style={{ fontSize:13.5, fontWeight:700, color:"var(--t-text-primary)", wordBreak:"break-all" }}>{f.original_name}</span>
                      {kb && <span style={{ fontSize:11.5, color:"var(--t-text-faint)" }}>{kb}</span>}
                    </div>
                    <div style={{ fontSize:12, color:"var(--t-text-muted)", marginBottom:6 }}>
                      {[f.signature, f.ticket_number ? `ticket #${f.ticket_number}` : null, f.sender, f.source].filter(Boolean).join(" · ")} · {timeAgo(f.created_at)}
                    </div>
                    <div style={{ fontSize:11, color:"var(--t-text-faint)", marginBottom:10, fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace", wordBreak:"break-all" }}>SHA-256: {f.sha256}</div>
                    <div style={{ display:"flex", gap:8 }}>
                      <button style={{ ...btn, color:"#dc2626", background:"#dc2626", borderColor:"#dc2626", flex:1, justifyContent:"center", filter:"none" }} disabled={fileBusy===f.id} onClick={() => deleteFile(f.id)}>
                        <Trash2 size={14} color="#fff"/> <span style={{ color:"#fff" }}>Delete permanently</span>
                      </button>
                      <button style={{ ...btn, justifyContent:"center" }} disabled={fileBusy===f.id} onClick={() => releaseFile(f.id)} title="Only if you're certain the file is safe">
                        <Check size={14}/> Release &amp; attach
                      </button>
                    </div>
                  </div>
                );
              })}
              {fileHistory.length > 0 && (
                <>
                  <div style={{ fontSize:11.5, fontWeight:700, color:"var(--t-text-muted)", textTransform:"uppercase", letterSpacing:".05em", marginTop:6 }}>Recently handled</div>
                  {fileHistory.map(f => (
                    <div key={f.id} style={{ ...card, padding:11, opacity:0.75, borderLeft:`3px solid ${f.status==="released" ? "#f59e0b" : "#64748b"}` }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                        <span style={{ background:(f.status==="released" ? "#d97706" : "#475569"), color:"#fff", fontSize:10.5, fontWeight:700, padding:"1px 8px", borderRadius:999, textTransform:"uppercase" }}>{f.status}</span>
                        <span style={{ fontSize:13, fontWeight:600, color:"var(--t-text-primary)", wordBreak:"break-all" }}>{f.original_name}</span>
                        <span style={{ color:"var(--t-text-muted)", fontSize:12 }}>{f.verdict}</span>
                        <span style={{ marginLeft:"auto", fontSize:11, color:"var(--t-text-faint)" }}>{timeAgo(f.resolved_at || f.created_at)}</span>
                      </div>
                      {f.ticket_number && <div style={{ fontSize:12, color:"var(--t-text-muted)", marginTop:3 }}>ticket #{f.ticket_number}{f.signature ? ` · ${f.signature}` : ""}</div>}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ───────── EMAIL QUARANTINE TAB ───────── */}
      {tab === "quarantine" && (
      <div style={panel}>
        <PanelHeader icon={<Mail size={18} color="#f59e0b" />} title="Email quarantine" count={unreviewed} countColor="#d97706">
          <div style={{ display:"flex", gap:8 }}>
            <button style={btn} onClick={() => setShow(s => !s)}>{showReviewed ? "Hide reviewed" : "Show reviewed"}</button>
            <button style={btn} onClick={loadRejections}><RefreshCw size={14}/></button>
          </div>
        </PanelHeader>
        {qLoading ? (
          <div style={{ ...card, padding:24, textAlign:"center", color:"var(--t-text-muted)" }}>Loading…</div>
        ) : items.length === 0 ? (
          <div style={{ ...card, padding:28, textAlign:"center", color:"var(--t-text-muted)" }}>
            <Mail size={26} style={{ opacity:0.4, marginBottom:6 }} /><div>{showReviewed ? "No quarantined emails." : "Nothing waiting for review. 🎉"}</div>
          </div>
        ) : (
          <div style={{ ...card, overflow:"hidden", overflowX: isMobile ? "auto" : undefined }}>
            <table style={{ width:"100%", minWidth: isMobile ? 720 : undefined, borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ background:"var(--t-surface)", textAlign:"left", color:"var(--t-text-muted)" }}>
                  <th style={{ padding:"10px 14px", fontWeight:600 }}>From</th>
                  <th style={{ padding:"10px 14px", fontWeight:600 }}>Subject</th>
                  <th style={{ padding:"10px 14px", fontWeight:600 }}>Preview</th>
                  <th style={{ padding:"10px 14px", fontWeight:600, whiteSpace:"nowrap" }}>Received</th>
                  <th style={{ padding:"10px 14px", fontWeight:600, textAlign:"right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id} style={{ borderTop:"1px solid var(--t-border)", opacity: it.reviewed ? 0.55 : 1 }}>
                    <td style={{ padding:"10px 14px", verticalAlign:"top" }}>
                      <div style={{ color:"var(--t-text-primary)", fontWeight:600 }}>{it.sender_name || "—"}</div>
                      <div style={{ color:"var(--t-text-muted)", fontSize:12 }}>{it.sender_email}</div>
                    </td>
                    <td style={{ padding:"10px 14px", verticalAlign:"top", color:"var(--t-text-primary)", maxWidth:240 }}>{it.subject || "(no subject)"}</td>
                    <td style={{ padding:"10px 14px", verticalAlign:"top", color:"var(--t-text-muted)", maxWidth:320 }}>{it.snippet || ""}</td>
                    <td style={{ padding:"10px 14px", verticalAlign:"top", color:"var(--t-text-muted)", whiteSpace:"nowrap" }} title={it.created_at}>{timeAgo(it.created_at)}</td>
                    <td style={{ padding:"10px 14px", verticalAlign:"top", textAlign:"right", whiteSpace:"nowrap" }}>
                      {!it.reviewed ? (
                        <button style={{ ...btn, color:"#059669" }} disabled={qBusy===it.id} onClick={() => markReviewed(it.id, true)}><Check size={14}/> Mark reviewed</button>
                      ) : (
                        <button style={btn} disabled={qBusy===it.id} onClick={() => markReviewed(it.id, false)}>Unreview</button>
                      )}
                      <button style={{ ...btn, color:"#dc2626", marginLeft:6 }} disabled={qBusy===it.id} onClick={() => removeRejection(it.id)}><Trash2 size={14}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* ───────── FTR TICKETS TAB ───────── */}
      {tab === "ftrtickets" && (
        <div style={panel}>
          <PanelHeader icon={<Bot size={18} color="#22c55e" />} title="FTR Tickets — resolved end-to-end by the AI" count={ftr.tickets.length} countColor="#16a34a">
            <button style={btn} onClick={loadFtr}><RefreshCw size={14}/></button>
          </PanelHeader>
          <p style={{ color:"var(--t-text-muted)", fontSize:12.5, marginTop:0, marginBottom:14 }}>
            Tickets the FTR completed and closed itself (work done + client confirmed). The weekly rate is FTR-closed as a share of <em>all</em> tickets completed that week.
          </p>

          {/* This week's headline */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:10, marginBottom:14 }}>
            <div style={{ ...card, padding:14 }}>
              <div style={{ fontSize:11.5, color:"var(--t-text-muted)", textTransform:"uppercase", letterSpacing:".05em", marginBottom:4 }}>This week</div>
              <div style={{ fontSize:30, fontWeight:800, color:"#22c55e", lineHeight:1.1 }}>{ftr.this_week.ftr_percent}%</div>
              <div style={{ fontSize:12, color:"var(--t-text-secondary)" }}>{ftr.this_week.ftr_completed} of {ftr.this_week.completed_total} completed tickets</div>
            </div>
            <div style={{ ...card, padding:14 }}>
              <div style={{ fontSize:11.5, color:"var(--t-text-muted)", textTransform:"uppercase", letterSpacing:".05em", marginBottom:4 }}>Closed by FTR this week</div>
              <div style={{ fontSize:30, fontWeight:800, color:"var(--t-text-primary)", lineHeight:1.1 }}>{ftr.this_week.ftr_completed}</div>
              <div style={{ fontSize:12, color:"var(--t-text-secondary)" }}>no technician needed</div>
            </div>
            <div style={{ ...card, padding:14 }}>
              <div style={{ fontSize:11.5, color:"var(--t-text-muted)", textTransform:"uppercase", letterSpacing:".05em", marginBottom:4 }}>All time</div>
              <div style={{ fontSize:30, fontWeight:800, color:"var(--t-text-primary)", lineHeight:1.1 }}>{ftr.ftr_all_time}</div>
              <div style={{ fontSize:12, color:"var(--t-text-secondary)" }}>tickets closed by FTR</div>
            </div>
          </div>

          {/* Weekly trend */}
          {ftr.weekly.length > 0 && (
            <>
              <div style={{ fontSize:11.5, fontWeight:700, color:"var(--t-text-muted)", textTransform:"uppercase", letterSpacing:".05em", marginBottom:8 }}>Weekly FTR completion rate</div>
              <div style={{ ...card, padding:12, marginBottom:16 }}>
                {ftr.weekly.map(w => (
                  <div key={w.week_start} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:7 }}>
                    <div style={{ width:96, fontSize:12, color:"var(--t-text-muted)", flexShrink:0 }}>
                      {new Date(w.week_start).toLocaleDateString(undefined,{ month:"short", day:"numeric" })}
                    </div>
                    <div style={{ flex:1, background:"var(--t-surface)", borderRadius:999, height:16, overflow:"hidden", border:"1px solid var(--t-border)" }}>
                      <div style={{ width:`${Math.min(100, w.ftr_percent)}%`, height:"100%", background:"linear-gradient(90deg,#16a34a,#22c55e)" }} />
                    </div>
                    <div style={{ width:130, fontSize:12, color:"var(--t-text-secondary)", textAlign:"right", flexShrink:0 }}>
                      <strong style={{ color:"var(--t-text-primary)" }}>{w.ftr_percent}%</strong> · {w.ftr_completed}/{w.completed_total}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Completed ticket log */}
          <div style={{ fontSize:11.5, fontWeight:700, color:"var(--t-text-muted)", textTransform:"uppercase", letterSpacing:".05em", marginBottom:8 }}>Completed by FTR</div>
          {ftrLoading ? (
            <div style={{ ...card, padding:24, textAlign:"center", color:"var(--t-text-muted)" }}>Loading…</div>
          ) : ftr.tickets.length === 0 ? (
            <div style={{ ...card, padding:28, textAlign:"center", color:"var(--t-text-muted)" }}>
              <Bot size={26} style={{ opacity:0.4, marginBottom:6 }} />
              <div>No FTR-closed tickets yet.</div>
              <div style={{ fontSize:12, marginTop:4 }}>They appear here once the AI resolves a ticket end-to-end and closes it.</div>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {ftr.tickets.map(t => (
                <div key={t.id} style={{ ...card, padding:12, borderLeft:"3px solid #22c55e" }}>
                  <div style={{ fontSize:12, color:"var(--t-text-muted)", marginBottom:3 }}>
                    {t.ticket_number ? `Ticket #${t.ticket_number}` : "—"} · {t.company_name || "—"}
                    {t.contact_name ? ` · ${t.contact_name}` : (t.contact_email ? ` · ${t.contact_email}` : "")}
                    {" · closed "}{timeAgo(t.ftr_completed_at)}
                  </div>
                  <div style={{ color:"var(--t-text-primary)", fontWeight:700, fontSize:13.5, marginBottom:4 }}>
                    <CheckCircle2 size={14} color="#22c55e" style={{ verticalAlign:"-2px", marginRight:6 }} />
                    {t.subject || "(no subject)"}
                  </div>
                  {t.ftr_completion_note && (
                    <div style={{ fontSize:12.5, color:"var(--t-text-secondary)" }}>{t.ftr_completion_note}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
